import { useMemo } from "react";

import { Button } from "#/components/ui/button";
import { Card, CardContent } from "#/components/ui/card";
import { Separator } from "#/components/ui/separator";
import { Skeleton } from "#/components/ui/skeleton";
import type { CalendarMonthBucket, CalendarMonthDay } from "#/server/game/archiveCalendarMonth";
import { applyBestScoresToMonthDays } from "#/server/game/scoreRecords";

import { DayVariantCell } from "./-archive.calendarDayVariant";
import { CalendarMonthHeatmap } from "./-archive.calendarHeatmap";
import { formatCalendarDayBadge } from "./-play.labels";

function CalendarDayLabel({ calendarDate }: { calendarDate: string }) {
  const { day: dayLabel, month: monthLabel } = formatCalendarDayBadge(calendarDate);
  const monthLower = monthLabel.toLowerCase();

  return (
    <div className="flex flex-col items-center">
      <span className="text-xl font-semibold leading-none tabular-nums">{dayLabel}</span>
      <span className="mt-0.5 text-sm font-medium lowercase text-muted-foreground">
        {monthLower}
      </span>
    </div>
  );
}

function CalendarDayCard({ day }: { day: CalendarMonthDay }) {
  const standardCompleted = day.standard.bestDays !== null;
  const premiumCompleted = day.premium.bestDays !== null;

  return (
    <div className="flex w-full flex-col gap-2 md:flex-row md:items-stretch md:gap-1">
      <DayVariantCell
        variantLabel="STANDARD"
        mapId={day.standard.mapId}
        bestDays={day.standard.bestDays}
        completed={standardCompleted}
        className="order-2 md:order-1 md:flex-1"
      />

      <div className="order-1 flex shrink-0 items-center justify-center py-0.5 md:order-2 md:w-14 md:py-0">
        <CalendarDayLabel calendarDate={day.calendarDate} />
      </div>

      <DayVariantCell
        variantLabel="PREMIUM"
        mapId={day.premium.mapId}
        bestDays={day.premium.bestDays}
        completed={premiumCompleted}
        className="order-3 md:flex-1"
      />
    </div>
  );
}

function CalendarMonthNav({
  monthLabel,
  monthKeys,
  visibleMonthIndex,
  onSelectMonth,
}: {
  monthLabel: string;
  monthKeys: string[];
  visibleMonthIndex: number;
  onSelectMonth: (monthKey: string) => void;
}) {
  const monthCount = monthKeys.length;

  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onSelectMonth(monthKeys[Math.min(visibleMonthIndex + 1, monthCount - 1)]!)}
        disabled={visibleMonthIndex >= monthCount - 1}
      >
        ← Older
      </Button>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {monthLabel}
      </h3>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onSelectMonth(monthKeys[Math.max(visibleMonthIndex - 1, 0)]!)}
        disabled={visibleMonthIndex <= 0}
      >
        Newer →
      </Button>
    </div>
  );
}

type CalendarMonthPanelProps = {
  visibleMonth: CalendarMonthBucket;
  monthKeys: string[];
  visibleMonthIndex: number;
  onSelectMonth: (monthKey: string) => void;
  monthScoresByMapId?: Record<string, number>;
  todayStr: string;
};

function CalendarMonthDayList({ monthKey, days }: { monthKey: string; days: CalendarMonthDay[] }) {
  return (
    <div className="space-y-3">
      {days.map((day) => (
        <CalendarDayCard key={`${monthKey}-${day.dayNumber}`} day={day} />
      ))}
    </div>
  );
}

function CalendarMonthPanel({
  visibleMonth,
  monthKeys,
  visibleMonthIndex,
  onSelectMonth,
  monthScoresByMapId,
  todayStr,
}: CalendarMonthPanelProps) {
  const daysWithScores = useMemo(
    () =>
      applyBestScoresToMonthDays({
        days: visibleMonth.days,
        scoresByMapId: monthScoresByMapId ?? {},
      }),
    [visibleMonth.days, monthScoresByMapId],
  );

  return (
    <Card>
      <CardContent>
        <CalendarMonthNav
          monthLabel={visibleMonth.monthLabel}
          monthKeys={monthKeys}
          visibleMonthIndex={visibleMonthIndex}
          onSelectMonth={onSelectMonth}
        />
        <CalendarMonthHeatmap
          monthKey={visibleMonth.monthKey}
          todayStr={todayStr}
          days={daysWithScores}
        />
        <Separator className="my-3 md:hidden" />
        <CalendarMonthDayList monthKey={visibleMonth.monthKey} days={daysWithScores} />
      </CardContent>
    </Card>
  );
}

function CalendarMonthSkeleton({ monthLabel }: { monthLabel: string }) {
  return (
    <Card>
      <CardContent>
        <h3 className="mb-3 text-center text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {monthLabel}
        </h3>
        <Skeleton className="mb-4 h-16 w-full" />
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

export function ArchiveCalendarSection({
  monthKeys,
  visibleMonth,
  visibleMonthIndex,
  onSelectMonth,
  monthScoresByMapId,
  todayStr,
  isCalendarPending,
  pendingMonthLabel,
}: {
  monthKeys: string[];
  visibleMonth: CalendarMonthBucket | null;
  visibleMonthIndex: number;
  onSelectMonth: (monthKey: string) => void;
  monthScoresByMapId?: Record<string, number>;
  todayStr: string;
  isCalendarPending: boolean;
  pendingMonthLabel?: string;
}) {
  if (monthKeys.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground">No daily maps are available yet.</p>
    );
  }

  if (isCalendarPending || !visibleMonth) {
    return <CalendarMonthSkeleton monthLabel={pendingMonthLabel ?? "Loading…"} />;
  }

  return (
    <CalendarMonthPanel
      visibleMonth={visibleMonth}
      monthKeys={monthKeys}
      visibleMonthIndex={visibleMonthIndex}
      onSelectMonth={onSelectMonth}
      monthScoresByMapId={monthScoresByMapId}
      todayStr={todayStr}
    />
  );
}
